/**
 * Posting Log Page
 *
 * Reports → Posting Log
 *
 * Endpoints:
 *   GET  /api/costing-transactions → CostingTransactionLog[]
 *   POST /api/costing-transactions/{logId}/resend → retry a failed line
 */
import React, { useCallback, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '@/components/blocks/page-header';
import { ReportNoAccessCard } from '@/components/shared/reports/ReportNoAccessCard';
import { usePermissions } from '@/contexts/PermissionContext';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import { useReportFilterOptions } from '@/hooks/useReportFilterOptions';
import { useReportColumns } from '@/hooks/useReportColumns';
import { Database, AlertCircle } from 'lucide-react';
import { POSTING_DEFAULT_COLUMNS, PAGE_SIZE } from '@/utils/PostingLog/constants';
import { normalisePageSize } from '@/utils/reportPageSize';
import { usePostingLogFilters } from '@/hooks/PostingLog/usePostingLogFilters';
import { usePostingLogData } from '@/hooks/PostingLog/usePostingLogData';
import { usePostingLogRetry } from '@/hooks/PostingLog/usePostingLogRetry';
import { usePostingLogExport } from '@/hooks/PostingLog/usePostingLogExport';
import { SummaryBar } from './SummaryBar';
import { RetryDialog } from './RetryDialog';
import { DrilldownPanel } from './DrilldownPanel';
import { ResultsTable } from './ResultsTable';
import { IntegrationBanner } from './IntegrationBanner';
import { PostingLogToolbar } from './PostingLogToolbar';
import { PostingLogFilterChips } from './PostingLogFilterChips';
import { PostingLogFilterSheet } from './PostingLogFilterSheet';

const PostingLogHeader: React.FC = () => (
  <PageHeader
    title="Posting Log"
    subtitle="ERP posting status for costing transactions"
    size={36}
    showActions={false}
    showBreadcrumb={false}
    icon={<Database />}
  />
);

export const PostingLogPage: React.FC = () => {
  const { canViewPostingLog, canExportPostingLog, canRetryPostingLog } = usePermissions();
  const { currentCompany } = useCompany();

  const canView     = canViewPostingLog();
  const allowExport = canExportPostingLog();
  const allowRetry  = canRetryPostingLog();

  const columns = useReportColumns(POSTING_DEFAULT_COLUMNS);

  // Drill-down panel
  const [drilldownId,   setDrilldownId]   = useState<string | null>(null);
  const [drilldownOpen, setDrilldownOpen] = useState(false);

  const filterOptions = useReportFilterOptions(currentCompany?.id, { enabled: canView, includeCostCentres: true });
  const filters = usePostingLogFilters();
  const { matchesNothing, setPage } = filters;
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(normalisePageSize(size, PAGE_SIZE));
    setPage(1);
  }, [setPage]);
  const {
    summary, summaryError, rows, totalCount, currentPage, isLoading, loadError, accessDenied, refreshKey, refresh,
    integrationConfigured, retryDisabledReason,
  } = usePostingLogData({
    canView, query: filters.query, page: filters.page, pageSize,
    sortKey: filters.sortKey, sortDir: filters.sortDir, matchesNothing, companyId: currentCompany?.id,
  });
  const {
    retryingIds, retryDialogOpen, setRetryDialogOpen, retryTarget,
    handleBulkRetry, handleRetryConfirm,
  } = usePostingLogRetry({ allowRetry, integrationConfigured, onRetried: refresh });
  const { isExporting, handleExport } = usePostingLogExport({ allowExport, matchesNothing, query: filters.query });

  // ── Drill-down ────────────────────────────────────────────────────────────────
  const handleTimesheetClick = useCallback((timesheetId: string) => {
    setDrilldownId(timesheetId);
    setDrilldownOpen(true);
  }, []);

  // ── Permission gate ───────────────────────────────────────────────────────────
  if (!canView || accessDenied) {
    return (
      <div className="space-y-6">
        <PostingLogHeader />
        <ReportNoAccessCard />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PostingLogHeader />

      <SummaryBar
        timesheets={summary?.timesheetCount ?? 0}
        posted={summary?.postedCount ?? 0}
        failed={summary?.failedCount ?? 0}
      />

      {/* Main card */}
      <Card>
        <CardContent className="pt-6 pb-3 px-6">
          <PostingLogToolbar
            filters={filters}
            columns={columns}
            allowExport={allowExport}
            isExporting={isExporting}
            onExport={handleExport}
          />

          <PostingLogFilterChips filters={filters} filterOptions={filterOptions} />

          {!integrationConfigured && <IntegrationBanner />}

          {(loadError || summaryError) && (
            <div className="flex items-start gap-2 rounded-md border border-error-200 bg-error-50 p-3 text-sm text-error-800 dark:border-error-800 dark:bg-error-900/20 dark:text-error-300 mb-4">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{loadError || summaryError}</span>
            </div>
          )}

          {/* Results Table */}
          <ResultsTable
            data={rows}
            totalCount={totalCount}
            isLoading={isLoading}
            onTimesheetClick={handleTimesheetClick}
            sortKey={filters.sortKey}
            sortDir={filters.sortDir}
            onSort={filters.handleSort}
            page={currentPage}
            onPageChange={filters.setPage}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            columnVisibility={columns.columnVisibility}
          />
        </CardContent>
      </Card>

      <PostingLogFilterSheet filters={filters} filterOptions={filterOptions} />

      {/* Retry Confirmation Dialog */}
      <RetryDialog
        open={retryDialogOpen}
        onOpenChange={setRetryDialogOpen}
        count={retryTarget.length}
        onConfirm={handleRetryConfirm}
      />

      {/* Drill-down Panel */}
      <DrilldownPanel
        timesheetId={drilldownId}
        open={drilldownOpen}
        onOpenChange={setDrilldownOpen}
        allowRetry={allowRetry}
        allowExport={allowExport}
        refreshKey={refreshKey}
        retryingIds={retryingIds}
        retryDisabledReason={retryDisabledReason}
        onBulkRetry={handleBulkRetry}
      />
    </div>
  );
};
